<template>
    <SwLayout>
        <div class="dm-wizard-field" v-if="presentation && presentation.content">
            <div class="form fv-plugins-bootstrap fv-plugins-framework">
                <div
                    class="form-group fv-plugins-icon-container border-bottom mb-6 pb-5"
                >
                    <h5>{{ translate("Tell us a bit about yourself.") }}</h5>

                    <div class="mb-4">
                        <TinyMce
                            v-model="presentation.content.biography"
                            toolbar="bold italic underline"
                            :purifyContent="true"
                            @change="validateAndSave()"
                        />

                        <WordCountWidget
                            :word_count="wordCount"
                            recommended_length="200"
                        />
                    </div>
                </div>

                <div
                    class="form-group fv-plugins-icon-container border-bottom mb-6 pb-5"
                >
                    <h5>{{ translate("Send a personal note") }}</h5>

                    <div class="mb-4">
                        <TinyMce
                            id="dm-editor-personal-note"
                            v-model="submission.temp_personal_note"
                            toolbar="bold italic underline"
                            :purifyContent="true"
                            @change="saveSubmissionWithoutStep()"
                        />
                    </div>
                </div>

                <div
                    class="d-inline-flex align-items-center dm-cursor-pointer mt-6 mb-4"
                    @click="
                        showAdditionalInformation = !showAdditionalInformation
                    "
                >
                    <h4 class="font-weight-bold text-dark mb-0">
                        {{ translate("Additional information (optional)") }}
                    </h4>
                    <span class="svg-icon svg-icon-lg ml-3">
                        <AngleDownSvg v-show="!showAdditionalInformation" />
                        <AngleUpSvg v-show="showAdditionalInformation" />
                    </span>
                </div>

                <div v-if="showAdditionalInformation">
                    <div
                        class="form-group fv-plugins-icon-container pb-6 border-bottom"
                    >
                        <label>{{
                            translate("Writing Course attended")
                        }}</label>

                        <button
                            v-if="showWrittingCourse"
                            class="btn btn-xs btn-icon btn-light-danger ml-3"
                            @click="removeWrittingCourse()"
                        >
                            <i class="icon-sm las la-times"></i>
                        </button>

                        <input
                            v-if="showWrittingCourse"
                            type="text"
                            class="form-control form-control-solid form-control-lg"
                            id="dm-manuscript-courses"
                            v-model="presentation.author_credentials.writing_course_attended"
                            @change="savePresentationWithoutStep()"
                        />

                        <div v-else class="d-block">
                            <button
                                class="btn btn-success dm-add-new-previous-book"
                                @click="
                                    showWrittingCourse = !showWrittingCourse
                                "
                            >
                                {{ translate("Add") }}
                            </button>
                        </div>
                    </div>

                    <div
                        class="form-group fv-plugins-icon-container pb-6 border-bottom"
                    >
                        <label>{{
                            translate("Editorial Consultant Used")
                        }}</label>

                        <button
                            v-if="showEditorialConsultant"
                            class="btn btn-xs btn-icon btn-light-danger ml-3"
                            @click="removeEditorialConsultant()"
                        >
                            <i class="icon-sm las la-times"></i>
                        </button>

                        <input
                            v-if="showEditorialConsultant"
                            type="text"
                            class="form-control form-control-solid form-control-lg"
                            id="dm-manuscript-consultants"
                            v-model="presentation.author_credentials.editorial_consultant_used"
                            @change="savePresentationWithoutStep()"
                        />

                        <div v-else class="d-block">
                            <button
                                class="btn btn-success dm-add-new-previous-book"
                                @click="
                                    showEditorialConsultant =
                                        !showEditorialConsultant
                                "
                            >
                                {{ translate("Add") }}
                            </button>
                        </div>
                    </div>

                    <div
                        class="form-group fv-plugins-icon-container pb-6 border-bottom"
                    >
                        <label>{{
                            translate("Previously Published books")
                        }}</label>
                        <PreviouslyPublishedBooks
                            :previous_published_books="presentation.author_credentials.previous_published_books"
                            @booksUpdated="savePresentationWithoutStep()"
                        />
                    </div>

                    <div
                        class="form-group fv-plugins-icon-container pb-6 border-bottom"
                    >
                        <label>{{ translate("Prizes and Grants") }}</label>
                        <PrizesAndGrants
                            :prizes_and_grants="presentation.author_credentials.prizes_and_grants"
                            @prizesUpdated="savePresentationWithoutStep()"
                        />
                    </div>
                </div>

                <div class="d-flex mt-6">
                    <div
                        class="d-inline-flex align-items-center dm-cursor-pointer mb-4"
                        @click="showSoMe = !showSoMe"
                    >
                        <h4 class="font-weight-bold text-dark mb-0">
                            {{ translate("Social Media and website (optional)") }}
                        </h4>

                        <span class="svg-icon svg-icon-lg ml-3">
                            <AngleDownSvg v-show="!showSoMe" />
                            <AngleUpSvg v-show="showSoMe" />
                        </span>
                    </div>
                </div>

                <div v-if="showSoMe">
                    <SocialLinks />
                </div>
            </div>
        </div>
    </SwLayout>
</template>

<script>
import TinyMce from "@shared/TinyMce.vue";
import WordCountWidget from "@shared/WordCountWidget.vue";
import { validationMixin } from "@/mixins/validationMixin.js";
import PrizesAndGrants from "./partials/PrizesAndGrants.vue";
import PreviouslyPublishedBooks from "./partials/previously-published-books/PreviouslyPublishedBooks.vue";
import { useSubmissionWizardStore } from "@storesSw/SubmissionWizardStore";
import { mapWritableState, mapActions } from "pinia";
import SocialLinks from "./partials/SocialLinks.vue";
import AngleDownSvg from "@/svg/AngleDownSvg.vue";
import AngleUpSvg from "@/svg/AngleUpSvg.vue";
import { getDistinctWordsCountInString } from "@/utilities/utilities.js";

export default {
    mixins: [validationMixin],

    components: {
        TinyMce,
        WordCountWidget,
        PrizesAndGrants,
        PreviouslyPublishedBooks,
        SocialLinks,
        AngleDownSvg,
        AngleUpSvg,
    },

    data() {
        return {
            minLength: 1, // words
            showWrittingCourse: false,
            showEditorialConsultant: false,
            showAdditionalInformation: false,
            showSoMe: false,
        };
    },

    computed: {
        ...mapWritableState(useSubmissionWizardStore, [
            "presentation",
            "submission",
            "bottom_navigation_rules",
        ]),

        isValid() {
            return this.biographyValid;
        },

        biographyValid() {
            return this.wordCount >= this.minLength;
        },

        wordCount() {
            if (!this.presentation) return 0;
            if (!this.presentation.content) return 0;

            return this.getDistinctWordsCountInString(this.presentation.content.biography);
        },
    },

    watch: {
        isValid: function (newState, oldState) {
            if (newState) {
                var self = this;
                setTimeout(function () {
                    self.savePresentation();
                }, 1000);
            }
        },

        "presentation.writing_course_attended"(newVal) {
            if (newVal) {
                this.showWrittingCourse = true;
            }
        },

        "presentation.editorial_consultant_used"(newVal) {
            if (newVal) {
                this.showEditorialConsultant = true;
            }
        },
    },

    methods: {
        getDistinctWordsCountInString,

        ...mapActions(useSubmissionWizardStore, [
            "savePresentation",
            "saveSubmissionWithoutStep",
            "savePresentationWithoutStep",
        ]),

        validateAndSave() {
            this.validate();

            if (this.isValid) this.savePresentation();
        },

        removeWrittingCourse() {
            this.presentation.content.writing_course_attended = "";
            this.showWrittingCourse = false;
            this.savePresentationWithoutStep();
        },

        removeEditorialConsultant() {
            this.presentation.content.editorial_consultant_used = "";
            this.showEditorialConsultant = false;
            this.savePresentationWithoutStep();
        },
    },
};
</script>
